import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "../../environments/environment";
import { finalize } from "rxjs/operators";
import { Router } from "@angular/router";
import { DomainService } from "./domain.service";
import { MultiFactorService } from "./multi-factor.service";
import { StorageService } from "./storage.service";
import { BetConfigService } from "./bet-config.service";

export interface ApiResponse {
  result: any[];
  status: any;
}
@Injectable({ providedIn: "root" })
export class ApiService {
  $tokenRefreshed = new Subject<boolean>();
  $refreshToken = new Subject<boolean>();
  $isAuthenticated = new Subject<boolean>();
  userToken$ = new BehaviorSubject<any>(null);
  isCasinoBlocked$ = new BehaviorSubject<boolean>(false);
  router = inject(Router);
  domainService = inject(DomainService);
  apiCallDurations$ = new Map<string, number>();
  mfaService = inject(MultiFactorService);
  storage = inject(StorageService);
  betConfigService = inject(BetConfigService);

  constructor(private http: HttpClient) {
    this.$refreshToken.subscribe((data: boolean) => {
      this.performTokenRefresh();
    });
    const stored = this.getUserToken();
    this.isCasinoBlocked$.next(stored?.user?.IS_BLOCKED_CASINO === "true");
  }

  handleRedirection() {
    const authToken = this.userToken$.getValue();
    const isUser2FAVerified = authToken?.user?.IS_2FA_VERIFIED === "true";
    const isUser2FAEnabled = authToken?.user?.IS_2FA_ENABLED === "true";

    // console.log("---isUser2FAEnabled", isUser2FAEnabled);
    // console.log("---isUser2FAVerified", isUser2FAVerified);

    if (isUser2FAEnabled) {
      if (isUser2FAVerified) {
        this.mfaService.showMfaModal(authToken);
      } else {
        this.mfaService.showMfaSetupModal(authToken);
      }
    } else {
      this.saveUserToken(authToken, true);
      this.router.navigate(["/app"]);
    }
  }

  handleSignOut() {
    this.logoutUser()
      .pipe(
        finalize(() => {
          // Ensure navigation to login happens no matter what
          this.$isAuthenticated.next(false);
          this.router.navigate(["/login"]);
        }),
      )
      .subscribe({
        next: (response: any) => {
          // Optional: Handle the success case, if needed
          console.log("Logout successful:", response);
        },
        error: (error: any) => {
          // Optional: Handle the error case, if needed
          console.error("Logout failed:", error);
        },
      });
  }

  clearUserToken() {
    // sessionStorage.removeItem("app");
    // localStorage.removeItem("lastActivityTime");
    this.storage.removeItem("app");
    this.storage.removeItem("lastActivityTime");
    this.storage.removeItem("betConfig");
  }

  saveUserToken(data: any, isLogin = false): boolean {
    // sessionStorage.setItem("app", JSON.stringify(data));
    // sessionStorage.setItem("locale", data?.user?.LANGUAGE || "en");
    this.storage.setItem("app", data);
    const lang = data?.user?.LANGUAGE || "en";
    this.storage.setItem("locale", lang);

    const prevCookie = this.storage.getTranslateCookie();
    if (isLogin) {
      if (lang !== "en" || (prevCookie && prevCookie !== "")) {
        this.storage.setItem("login_redirect", "true");
      }
    }

    // Apply the translation cookie immediately on token load/login
    this.storage.clearTranslateCookie();
    let isTranslated = false;
    if (lang !== "en") {
      this.storage.setTranslateCookie(lang);
      isTranslated = true;
    }

    this.betConfigService.updateBetConfig(data?.user?.BET_CONFIG);
    this.isCasinoBlocked$.next(data?.user?.IS_BLOCKED_CASINO === "true");
    // let oData = JSON.parse(atob(data.access_token.split(".")[1]));
    // let oUserFields = JSON.parse(oData.user_fields);
    // this.oRoles = oUserFields?.ROLES;
    // this.oPermissions = oUserFields?.PERMISSIONS;
    return isTranslated;
  }

  getUserToken() {
    // const oSessionData = sessionStorage.getItem("app");
    const oSessionData = this.storage.getItem<any>("app");
    if (oSessionData != null) return oSessionData;
    return null;
  }

  updateWlPermissions(permissions: string) {
    const token = this.getUserToken();
    if (token) {
      this.storage.setItem("app", {
        ...token,
        user: { ...token.user, PERMISSIONS: permissions },
      });
    }
  }

  updateCasinoBlockedStatus(blocked: boolean) {
    const token = this.getUserToken();
    if (token) {
      this.storage.setItem("app", {
        ...token,
        user: { ...token.user, IS_BLOCKED_CASINO: blocked ? "true" : "false" },
      });
      this.isCasinoBlocked$.next(blocked);
    }
  }

  getMaintenanceMode() {
    // const maintenanceMode = sessionStorage.getItem("maintenanceMode");
    const maintenanceMode = this.storage.getItem("maintenanceMode");
    if (maintenanceMode != null) {
      return maintenanceMode;
    }

    return null;
  }

  getUserWalletId() {
    // const walletId = sessionStorage.getItem("walletId");
    const walletId = this.storage.getItem<string>("walletId");
    if (walletId != null) {
      return parseInt(walletId);
    }
    return null;
  }

  performTokenRefresh() {
    this.send2ServerRelative(environment.endpoints.tokenRenew, true).subscribe((response: any) => {
      // console.log(response);
      if (response) {
        let oUserData = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          user: response.user_fields,
        };
        this.saveUserToken(oUserData);
        this.$tokenRefreshed.next(true);
      }
    });
  }

  getHeaders(): any {
    return new HttpHeaders({
      "Content-Type": "application/json",
    });
  }

  async performLogin(uname: string, pwd: string, type: "demo" | "normal" = "normal"): Promise<boolean> {
    // console.log(uname, pwd);
    try {
      const data: any = await this.send2ServerRelative(environment.endpoints.auth2, true, {
        USERNAME: uname,
        PASSWORD: pwd,
        DOMAIN_NAME: this.domainService.getDomain(),
      }).toPromise();
      // console.log(data);
      let oUserData = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: data.user_fields,
      };

      if (type === "demo") {
        this.saveUserToken(oUserData, true);
      } else {
        this.userToken$.next(oUserData);
      }

      return true;
    } catch (error) {
      console.error(error);
      if (JSON.stringify(error)?.includes("Invalid Credentials")) this.reportFailedAttempt({ USERNAME: uname }).subscribe();
      return false;
    }
  }

  send2ServerRelative(sPartialURL: string, bPost: boolean = false, oItem: any = null): Observable<any> {
    const httpOptions = {
      headers: this.getHeaders(),
      withCredentials: sPartialURL.includes("renew"),
    };
    // console.log(httpOptions);
    if (bPost) {
      return this.http.post<ApiResponse>(environment.sURL + sPartialURL, oItem, httpOptions);
    }
    return this.http.get<ApiResponse>(environment.sURL + sPartialURL, httpOptions);
  }

  fetchUserAccount(params: any) {
    return this.http.get(`${environment.sURL}${environment.endpoints.userAccount}/${params?.id}`);
  }

  fetchWlPermissions(domain: string): Observable<any> {
    return this.http.get(`${environment.sURL}${environment.endpoints.wlPermissions}/${domain}`);
  }

  register(data: any): Observable<any> {
    return this.http.post(`${environment.sURL}${environment.endpoints.selfRegister}`, {
      ...data,
      DOMAIN_NAME: this.domainService.getDomain(),
    });
  }

  updateUserAccount(params: any) {
    return this.http.post(`${environment.sURL}${environment.endpoints.updateAccount}`, params);
  }

  logoutUser() {
    localStorage.removeItem("placing_bet_flag");

    return this.http.post(`${environment.sURL}${environment.endpoints.logout}`, null);
  }

  reportFailedAttempt(params?: any) {
    return this.http.post(`${environment.sURL}${environment.endpoints.failedAttempts}`, params);
  }
}
